// Two-step flow: (1) scan or type a barcode and try to auto-populate the form
// from OpenFoodFacts; (2) review/edit details and save. Users can also skip
// the barcode step entirely for items without a label.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../api";
import { BarcodeScanner } from "../components/BarcodeScanner";
import type { Product } from "../types";

interface FormState {
  barcode: string;
  name: string;
  brand: string;
  category: string;
  imageUrl: string;
  quantity: string;
  unit: string;
  bestBefore: string;
  location: string;
  notes: string;
}

const EMPTY: FormState = {
  barcode: "",
  name: "",
  brand: "",
  category: "",
  imageUrl: "",
  quantity: "1",
  unit: "",
  bestBefore: "",
  location: "",
  notes: "",
};

export function AddItem() {
  const navigate = useNavigate();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [existingProduct, setExistingProduct] = useState<Product | null>(null);
  const [lookupStatus, setLookupStatus] =
    useState<"idle" | "loading" | "found" | "miss" | "error">("idle");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<{
    locations: string[];
    categories: string[];
  }>({ locations: [], categories: [] });
  const nameRef = useRef<HTMLInputElement>(null);
  // The barcode we most recently kicked off a lookup for. The debounced
  // manual-typing effect below checks this so it doesn't fire a *second*
  // lookup for a code that a scan just filled in — that duplicate request
  // races the first one and, if it hits a transient OpenFoodFacts hiccup,
  // flips the status to "not found" while the form is still prefilled.
  const lastLookupRef = useRef<string | null>(null);

  // When a barcode lookup misses, jump the cursor straight into the name
  // field — that's the only thing the user has to fill in to proceed.
  useEffect(() => {
    if (lookupStatus === "miss") nameRef.current?.focus();
  }, [lookupStatus]);

  // Pull existing locations / categories so the dropdowns auto-populate as
  // the household builds out its vocabulary.
  useEffect(() => {
    let cancelled = false;
    api.getSuggestions().then(
      (s) => !cancelled && setSuggestions(s),
      () => {} // ignore — empty suggestions just means a plain text input
    );
    return () => {
      cancelled = true;
    };
  }, []);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleBarcode(barcode: string) {
    lastLookupRef.current = barcode;
    setScannerOpen(false);
    update("barcode", barcode);
    setLookupStatus("loading");
    setExistingProduct(null);
    try {
      // First, see if this household already has a product for this barcode.
      const known = await api.getProductByBarcode(barcode);
      if (known.product) {
        setExistingProduct(known.product);
        setForm((f) => ({
          ...f,
          barcode,
          name: known.product!.name,
          brand: known.product!.brand ?? "",
          category: known.product!.category ?? "",
          imageUrl: known.product!.image_url ?? "",
          // Unit: keep what the user typed; else the last lot's unit; else the
          // product's stored default.
          unit: f.unit || known.last_unit || (known.product!.default_unit ?? ""),
          // Default the quantity to whatever the last lot of this product was
          // (e.g. re-scanning a 5-bar box → 5 again), unless the user already
          // typed a non-default quantity.
          quantity:
            known.last_quantity != null &&
            (f.quantity === "" || f.quantity === "1")
              ? String(known.last_quantity)
              : f.quantity,
          // Pre-fill location from the last lot of this product (e.g. "Pantry
          // shelf 2"), but only if the user hasn't already typed something.
          location: f.location || (known.last_location ?? ""),
        }));
        setLookupStatus("found");
        return;
      }
      // Fall back to OpenFoodFacts.
      const lookup = await api.lookupBarcode(barcode);
      if (lookup.found) {
        setForm((f) => ({
          ...f,
          barcode,
          name: lookup.name ?? f.name,
          brand: lookup.brand ?? f.brand,
          category: lookup.category ?? f.category,
          imageUrl: lookup.image_url ?? f.imageUrl,
          unit: lookup.default_unit ?? f.unit,
          // If OFF reports a countable pack ("24 bars"), prefill the count —
          // but never clobber a quantity the user already typed themselves.
          quantity:
            lookup.pack_quantity && (f.quantity === "" || f.quantity === "1")
              ? String(lookup.pack_quantity)
              : f.quantity,
        }));
        setLookupStatus("found");
      } else {
        setLookupStatus("miss");
      }
    } catch {
      // Network / timeout / our own API unreachable — distinct from "the
      // product database has no record", which is what "miss" means.
      setLookupStatus("error");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!form.name.trim()) {
      setErr("Please enter a product name.");
      return;
    }
    setSubmitting(true);
    try {
      await api.addInventory({
        product_id: existingProduct?.id,
        barcode: form.barcode.trim() || undefined,
        name: form.name.trim() || undefined,
        brand: form.brand.trim() || undefined,
        category: form.category.trim() || undefined,
        image_url: form.imageUrl.trim() || undefined,
        quantity: parseFloat(form.quantity) || 1,
        unit: form.unit.trim() || undefined,
        best_before: form.bestBefore || null,
        location: form.location.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      navigate("/", { replace: true });
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : "Failed to save";
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // If the user types a barcode manually, debounce a lookup.
  useEffect(() => {
    const code = form.barcode.trim();
    if (!/^\d{6,14}$/.test(code)) {
      setLookupStatus("idle");
      setExistingProduct(null);
      lastLookupRef.current = null;
      return;
    }
    // Already looked this one up — almost always because a scan just filled
    // it in and handleBarcode is mid-flight. Don't double-fire.
    if (code === lastLookupRef.current) return;
    const t = setTimeout(() => void handleBarcode(code), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.barcode]);

  return (
    <div className="mx-auto max-w-2xl p-4">
      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetect={handleBarcode}
      />

      <div className="mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="btn-primary flex-1"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7V5a2 2 0 0 1 2-2h2M21 7V5a2 2 0 0 0-2-2h-2M3 17v2a2 2 0 0 0 2 2h2M21 17v2a2 2 0 0 1-2 2h-2M7 8v8M11 8v8M15 8v8M19 8v8" />
          </svg>
          Scan barcode
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label" htmlFor="barcode">Barcode (optional)</label>
          <input
            id="barcode"
            inputMode="numeric"
            className="input font-mono"
            placeholder="Scan or type a barcode"
            value={form.barcode}
            onChange={(e) => update("barcode", e.target.value)}
          />
          <LookupHint
            status={lookupStatus}
            existing={existingProduct}
            onRetry={() => {
              const code = form.barcode.trim();
              if (/^\d{6,14}$/.test(code)) void handleBarcode(code);
            }}
          />
        </div>

        {form.imageUrl && (
          <div className="flex items-center gap-3">
            <img
              src={form.imageUrl}
              alt=""
              className="h-20 w-20 rounded-lg border border-stone-200 object-cover dark:border-stone-700"
            />
            <p className="text-xs text-stone-500 dark:text-stone-400">
              Image from OpenFoodFacts. Custom uploads are disabled in V1.
            </p>
          </div>
        )}

        <div>
          <label className="label" htmlFor="name">Product name</label>
          <input
            ref={nameRef}
            id="name"
            className="input"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="brand">Brand</label>
            <input
              id="brand"
              className="input"
              value={form.brand}
              onChange={(e) => update("brand", e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="category">Category</label>
            <input
              id="category"
              className="input"
              list="add-category-suggestions"
              placeholder="Snacks, Dairy, Produce…"
              value={form.category}
              onChange={(e) => update("category", e.target.value)}
            />
            <datalist id="add-category-suggestions">
              {suggestions.categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="qty">Quantity</label>
            <input
              id="qty"
              type="number"
              min="0"
              step="any"
              className="input"
              value={form.quantity}
              onChange={(e) => update("quantity", e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="unit">Unit</label>
            <input
              id="unit"
              className="input"
              placeholder="can, g, pack…"
              value={form.unit}
              onChange={(e) => update("unit", e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="label" htmlFor="bb">Best before</label>
          <div className="flex items-center gap-2">
            <input
              id="bb"
              type="date"
              className="input max-w-[14rem]"
              value={form.bestBefore}
              onChange={(e) => update("bestBefore", e.target.value)}
            />
            {form.bestBefore && (
              <button
                type="button"
                onClick={() => update("bestBefore", "")}
                className="rounded-md px-2 py-1 text-xs text-stone-600 hover:bg-stone-100 hover:text-stone-900 dark:text-stone-400 dark:hover:bg-stone-800 dark:hover:text-stone-100"
                aria-label="Clear expiry date"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div>
          <label className="label" htmlFor="loc">Location</label>
          <input
            id="loc"
            className="input"
            list="add-location-suggestions"
            placeholder="Pantry shelf 1, Fridge, Freezer…"
            value={form.location}
            onChange={(e) => update("location", e.target.value)}
          />
          <datalist id="add-location-suggestions">
            {suggestions.locations.map((l) => (
              <option key={l} value={l} />
            ))}
          </datalist>
        </div>

        <div>
          <label className="label" htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            className="input min-h-[60px]"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
          />
        </div>

        {err && <p className="text-sm text-red-600 dark:text-red-400">{err}</p>}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary flex-1" disabled={submitting}>
            {submitting ? "Saving…" : "Add to pantry"}
          </button>
        </div>
      </form>
    </div>
  );
}

function WarningIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="mt-0.5 h-4 w-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 9v4M12 17h.01" strokeLinecap="round" />
      <path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

function LookupHint({
  status,
  existing,
  onRetry,
}: {
  status: "idle" | "loading" | "found" | "miss" | "error";
  existing: Product | null;
  onRetry: () => void;
}) {
  if (status === "idle") return null;
  if (status === "loading")
    return (
      <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">Looking up…</p>
    );
  if (status === "found" && existing)
    return (
      <p className="mt-1 text-xs text-brand-700 dark:text-brand-400">
        Existing pantry product — adding a new lot to “{existing.name}”.
      </p>
    );
  if (status === "found")
    return (
      <p className="mt-1 text-xs text-brand-700 dark:text-brand-400">
        Found in OpenFoodFacts — fields prefilled below.
      </p>
    );
  if (status === "error")
    return (
      <div
        role="status"
        className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200"
      >
        <WarningIcon />
        <div className="space-y-1">
          <p>
            <strong className="font-semibold">
              Couldn’t reach the product database.
            </strong>{" "}
            Check your connection, or just fill in the details below.
          </p>
          <button
            type="button"
            onClick={onRetry}
            className="text-xs font-medium underline underline-offset-2 hover:no-underline"
          >
            Try again
          </button>
        </div>
      </div>
    );
  return (
    <div
      role="status"
      className="mt-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200"
    >
      <WarningIcon />
      <p>
        <strong className="font-semibold">Not found in any catalog.</strong>{" "}
        Type the product name below — everything else is optional.
      </p>
    </div>
  );
}
